import { Link } from "react-router-dom";
import { ArrowRight, Microphone, Play, NewspaperClipping } from "@phosphor-icons/react";
import { primaryCategory, postCategories } from "../lib/api";

const typeMeta = {
  article: { Icon: NewspaperClipping, label: "Article", accent: "text-white" },
  podcast: { Icon: Microphone, label: "Podcast", accent: "text-[#E36A53]" },
  digital: { Icon: Play, label: "Digital", accent: "text-[#4FB7D7]" },
};

export function PostCard({ post, size = "md" }) {
  const meta = typeMeta[post.type] || typeMeta.article;
  const { Icon } = meta;
  const large = size === "lg";

  return (
    <Link
      to={`/post/${post.slug}`}
      data-testid={`post-card-${post.slug}`}
      className="group flex flex-col border border-white/10 bg-[#0a0a0a] transition-colors hover:border-[#D4FF00]"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={post.cover_image}
          alt={post.title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute left-0 top-0 flex items-center gap-2 bg-black/80 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em]">
          <Icon size={14} weight="bold" className={meta.accent} />
          <span>{meta.label}</span>
        </div>
        {post.duration && (
          <div className="absolute bottom-3 right-3 bg-black/80 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">
            {post.duration}
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5 sm:p-6">
        <div className="flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00]">
          {postCategories(post).slice(0, 3).map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <h3 className={`font-display font-semibold leading-[1.05] tracking-tight text-white transition-colors group-hover:text-[#D4FF00] ${large ? "text-2xl sm:text-3xl md:text-4xl" : "text-lg sm:text-xl md:text-2xl"}`}>
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="text-sm leading-relaxed text-white/60 line-clamp-2">{post.excerpt}</p>
        )}
        <div className="mt-auto flex items-center justify-between pt-4 text-xs uppercase tracking-[0.2em] text-white/50">
          <span>{post.author}</span>
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
}

export function HeroCard({ post }) {
  if (!post) return null;
  const meta = typeMeta[post.type] || typeMeta.article;
  const { Icon } = meta;
  return (
    <Link
      to={`/post/${post.slug}`}
      data-testid="hero-article-link"
      className="group relative flex aspect-[4/5] w-full overflow-hidden border border-white/10 sm:aspect-[16/10] md:aspect-[16/9] lg:aspect-auto lg:h-full lg:min-h-[520px]"
    >
      <img
        src={post.cover_image}
        alt={post.title}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
      <div className="relative z-10 flex h-full w-full flex-col justify-end p-5 sm:p-8 md:p-12 lg:p-16">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00] sm:text-xs">
          <Icon size={14} weight="bold" />
          Featured · {primaryCategory(post)}
        </div>
        <h1 className="mt-3 max-w-4xl font-display text-xl font-bold leading-[1] tracking-tighter text-white sm:mt-6 sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
          {post.title}
        </h1>
        <p className="mt-2 hidden max-w-2xl text-sm leading-relaxed text-white/80 sm:mt-4 sm:block md:text-base">{post.excerpt}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/60 sm:mt-6 sm:gap-4 sm:text-xs">
          <span>{post.author}</span>
          <span className="h-1 w-1 bg-white/40" />
          <span>{post.duration}</span>
        </div>
      </div>
    </Link>
  );
}
